/**
 * Dependency Health Widget (issue #1651, part of epic #1650)
 *
 * Shows the health of the current user's team-scoped outbound service
 * dependency graph: total/healthy/warnings/critical counts plus the
 * individual detected issues (missing, deprecated, and circular
 * dependencies), matching `homepage-dashboard-spec.md`'s "Service Dependency
 * Health" widget. Follows the single-query + `useStatisticsWidget` pattern
 * shared by `CapabilityStatisticsWidget`/`TechnologyStatisticsWidget`.
 *
 * `refreshInterval` is 10 minutes, matching the spec's documented cache TTL
 * for this widget (`widget:dep-health:${scope}`, 10 min) — no backend cache
 * exists yet for any widget, so this is the widget's one observable proxy
 * for that documented refresh cadence.
 */

import { useDependencyHealth, type DependencyHealthIssue } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './DependencyHealthWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'dependency-health',
  title: 'Dependency Health',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the spec's documented cache TTL
  showRefresh: true,
  showExpand: false,
};

interface DependencyHealthStats {
  summary: {
    totalDependencies: number;
    healthy: number;
    warnings: number;
    critical: number;
  };
  issues: DependencyHealthIssue[];
}

type DependencyHealthQueryData = ReturnType<typeof useDependencyHealth>['data'];

function extractDependencyHealthStats(
  data: DependencyHealthQueryData
): DependencyHealthStats | null {
  if (!data?.dependencyHealth) {
    return null;
  }
  return data.dependencyHealth;
}

/** Maps an issue's severity to its icon; unrecognized severities fall back to a neutral info icon. */
function severityIcon(severity: string): string {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
}

export function DependencyHealthWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useDependencyHealth(),
    extractDependencyHealthStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="dependency-health-widget__empty">
          <IconElement name="hub" className="dependency-health-widget__empty-icon" />
          <p className="forge-typography--body2">No dependency data available</p>
        </div>
      </DashboardWidget>
    );
  }

  if (stats.summary.totalDependencies === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="dependency-health-widget__empty">
          <IconElement name="link" className="dependency-health-widget__empty-icon" />
          <p className="forge-typography--body2">
            Your team&apos;s services have no outbound dependencies to check
          </p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="dependency-health-widget__content">
        <div
          className="dependency-health-widget__summary-grid"
          data-testid="dependency-health-summary"
        >
          <div className="dependency-health-widget__summary-tile">
            <div className="dependency-health-widget__summary-count">
              {stats.summary.totalDependencies}
            </div>
            <div className="dependency-health-widget__summary-label forge-typography--label2">
              Total
            </div>
          </div>
          <div className="dependency-health-widget__summary-tile dependency-health-widget__summary-tile--healthy">
            <div className="dependency-health-widget__summary-count">{stats.summary.healthy}</div>
            <div className="dependency-health-widget__summary-label forge-typography--label2">
              Healthy
            </div>
          </div>
          <div className="dependency-health-widget__summary-tile dependency-health-widget__summary-tile--warning">
            <div className="dependency-health-widget__summary-count">{stats.summary.warnings}</div>
            <div className="dependency-health-widget__summary-label forge-typography--label2">
              Warnings
            </div>
          </div>
          <div className="dependency-health-widget__summary-tile dependency-health-widget__summary-tile--critical">
            <div className="dependency-health-widget__summary-count">{stats.summary.critical}</div>
            <div className="dependency-health-widget__summary-label forge-typography--label2">
              Critical
            </div>
          </div>
        </div>

        {stats.issues.length === 0 ? (
          <div className="dependency-health-widget__all-healthy">
            <IconElement
              name="check_circle"
              className="dependency-health-widget__all-healthy-icon"
            />
            <span className="forge-typography--body2">All dependencies healthy</span>
          </div>
        ) : (
          <ul
            className="dependency-health-widget__issue-list"
            data-testid="dependency-health-issues"
          >
            {stats.issues.map((issue, index) => (
              <li
                key={`${issue.service}-${issue.dependsOn}-${issue.type}-${index}`}
                className={`dependency-health-widget__issue dependency-health-widget__issue--${issue.severity}`}
              >
                <IconElement
                  name={severityIcon(issue.severity)}
                  className="dependency-health-widget__issue-icon"
                  aria-hidden="true"
                />
                <span className="dependency-health-widget__issue-message forge-typography--body2">
                  {issue.message}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardWidget>
  );
}
